import React from 'react';
import { Download } from 'lucide-react';
import { stickers } from '../data/stickers';

interface StickerCollectionProps {
  collectedStickers: number[];
}

export function StickerCollection({ collectedStickers }: StickerCollectionProps) {
  // Grupuj naklejki według kategorii
  const groupedStickers = collectedStickers.reduce((acc, stickerId) => {
    const sticker = stickers.find(s => s.id === stickerId);
    if (sticker) {
      if (!acc[sticker.category]) {
        acc[sticker.category] = [];
      }
      acc[sticker.category].push(sticker);
    }
    return acc;
  }, {} as Record<string, typeof stickers>);

  const getCategoryName = (category: string) => {
    switch (category) {
      case 'pokemon': return 'Pokemony';
      case 'cartoons': return 'Kreskówki';
      case 'animals': return 'Zwierzęta';
      case 'dragons': return 'Smoki';
      case 'space': return 'Kosmos';
      case 'nature': return 'Natura';
      case 'fantasy': return 'Fantazja';
      default: return category;
    }
  };

  const getCategoryProgress = (category: string) => {
    const totalInCategory = stickers.filter(s => s.category === category).length;
    const collectedInCategory = groupedStickers[category]?.length || 0;
    return `${collectedInCategory}/${totalInCategory}`;
  };

  const handleDownload = async (url: string, name: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${name.toLowerCase().replace(/\s+/g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Błąd podczas pobierania naklejki:', error);
      alert('Przepraszamy, wystąpił błąd podczas pobierania naklejki. Spróbuj ponownie później.');
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">Twoja kolekcja naklejek</h2>
      
      {Object.keys(groupedStickers).length === 0 ? (
        <p className="text-center text-gray-500">
          Rozwiązuj zadania, aby zdobyć naklejki!
        </p>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedStickers).map(([category, categoryStickers]) => (
            <div key={category}>
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-lg">{getCategoryName(category)}</h3>
                <span className="text-sm text-gray-600">
                  Zebrano: {getCategoryProgress(category)}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {categoryStickers.map((sticker) => (
                  <div
                    key={sticker.id}
                    className="relative aspect-square rounded-lg overflow-hidden hover:transform hover:scale-105 transition-transform group bg-white p-2 border border-gray-100 shadow-sm"
                  >
                    <img
                      src={sticker.url}
                      alt={sticker.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-1 text-xs text-center">
                      {sticker.name}
                    </div>
                    <button
                      onClick={() => handleDownload(sticker.url, sticker.name)}
                      className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
                      title="Pobierz naklejkę"
                    >
                      <Download className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}